import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  alpha,
  Collapse,
  Divider,
  Stack,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import SettingsIcon from "@mui/icons-material/Settings";
import ColorLens from "@mui/icons-material/ColorLens";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import CloudIcon from "@mui/icons-material/Cloud";
import { Language } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import { GlobalContext } from "../../context/GlobalContext";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VpnLockIcon from "@mui/icons-material/VpnLock";

const drawerWidth = 260;

const Sidebar = ({
  lang,
  theme,
  isMobile,
  selectedMenu,
  openSubmenus,
  mobileOpen,
  handleDrawerToggle,
  handleMenuClick,
  handleSubmenuClick,
  isDark,
}) => {
  const { handleLogout, web } = React.useContext(GlobalContext);

  const siteName = web?.site_name || "Admin Panel";
  const logoFile = web?.site_logo;

  // Auto-generate initials from site name (e.g. "TorrentCloud" → "TC")
  const initials = siteName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const menuItems = [
    {
      id: "dashboard",
      text: lang.dashboard || "Dashboard",
      icon: <DashboardIcon fontSize="small" />,
    },
    {
      id: "manage-users",
      text: lang.manageUsers || "Manage Users",
      icon: <PeopleIcon fontSize="small" />,
    },
    {
      id: "manage-plans",
      text: lang.managePlans || "Manage Plans",
      icon: <InventoryIcon fontSize="small" />,
    },
    {
      id: "orders",
      text: lang.orders || "Orders",
      icon: <ReceiptIcon fontSize="small" />,
    },
    {
      id: "payment-gateway",
      text: lang.paymentGateway || "Payment Gateway",
      icon: <PaymentIcon fontSize="small" />,
    },
    {
      id: "site-settings",
      text: lang.siteSettings || "Site Settings",
      icon: <SettingsIcon fontSize="small" />,
    },
    {
      id: "storage-settings",
      text: lang.storageSettings || "Storage",
      icon: <CloudIcon fontSize="small" />,
    },
    {
      id: "web-translation",
      text: lang.webTranslation || "Translation",
      icon: <Language fontSize="small" />,
    },
    {
      id: "web-theme",
      text: lang.webTheme || "Theme Manager",
      icon: <ColorLens fontSize="small" />,
    },
    {
      id: "support-tickets",
      text: lang.supportTickets || "Support Tickets",
      icon: <SupportAgentIcon fontSize="small" />,
    },
    {
      id: "email-templates",
      text: lang.emailTemplates || "Email Templates",
      icon: <EmailIcon fontSize="small" />, // already imported
    },
    {
      id: "proxy-settings",
      text: lang.proxySettings || "Proxy Settings",
      icon: <VpnLockIcon fontSize="small" />,
    },
    {
      id: "email-settings",
      text: lang.emailSettings || "Email Settings",
      icon: <EmailIcon fontSize="small" />,
    },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* ── Logo / Brand ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Logo box — real image or gradient initials fallback */}
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              overflow: "hidden",
              flexShrink: 0,
              ...(logoFile
                ? {
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    bgcolor: "transparent",
                  }
                : {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
                      theme.palette.primary.main,
                      0.7,
                    )} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }),
            }}
          >
            {logoFile ? (
              <Box
                component="img"
                src={`/media/${logoFile}`}
                alt={siteName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <Typography
                variant="caption"
                fontWeight={800}
                color="#fff"
                sx={{ fontSize: "0.75rem" }}
              >
                {initials}
              </Typography>
            )}
          </Box>

          {/* Site name + subtitle */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              {siteName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              Management Console
            </Typography>
          </Box>
        </Stack>

        {/* Mobile close button */}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider />

      {/* ── Nav Items ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1.5, px: 1.5 }}>
        <Typography
          variant="caption"
          color="text.disabled"
          fontWeight={700}
          sx={{
            px: 1,
            fontSize: "0.65rem",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            display: "block",
            mb: 1,
          }}
        >
          Main Menu
        </Typography>
        <List disablePadding>
          {menuItems.map((item) => {
            const isSelected =
              selectedMenu === item.id ||
              item.submenu?.some((s) => s.id === selectedMenu);
            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleMenuClick(item.id, !!item.submenu)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      minHeight: 42,
                      "&.Mui-selected": {
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.15,
                        )} 0%, ${alpha(
                          theme.palette.primary.main,
                          0.08,
                        )} 100%)`,
                        color: theme.palette.primary.main,
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.main,
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "20%",
                          bottom: "20%",
                          width: 3,
                          borderRadius: "0 3px 3px 0",
                          bgcolor: theme.palette.primary.main,
                        },
                      },
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 34,
                        color: isSelected
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    />
                    {item.submenu &&
                      (openSubmenus?.[item.id] ? (
                        <ExpandLessIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                      ))}
                  </ListItemButton>
                </ListItem>

                {item.submenu && (
                  <Collapse
                    in={openSubmenus?.[item.id]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      {item.submenu.map((sub) => (
                        <ListItem key={sub.id} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            selected={selectedMenu === sub.id}
                            onClick={() => handleSubmenuClick(sub.id)}
                            sx={{
                              borderRadius: 2,
                              py: 0.8,
                              pl: 3,
                              "&.Mui-selected": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: "primary.main",
                              },
                            }}
                          >
                            <ListItemText
                              primary={sub.text}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: selectedMenu === sub.id ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* ── Bottom: Admin Info + Logout ───────────────────────────────────── */}
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            mb: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Avatar — logo thumbnail or letter fallback */}
            <Avatar
              src={logoFile ? `/media/${logoFile}` : undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.primary.main,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {!logoFile && initials[0]}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ fontSize: 12.5, lineHeight: 1.2 }}
              >
                {lang.administrator || "Administrator"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
                noWrap
              >
                admin@admin.com
              </Typography>
            </Box>
          </Stack>
        </Box>

        <ListItemButton
          onClick={() => handleLogout(true)}
          sx={{
            borderRadius: 2,
            color: "error.main",
            py: 0.8,
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: "error.main" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang.logout || "Logout"}
            primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            border: "none",
            boxShadow: "0 0 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            border: "none",
            boxShadow: isDark
              ? `1px 0 0 ${alpha("#ffffff", 0.06)}`
              : "1px 0 0 #f0f0f0",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
